import StepsData from "@/components/inventory/BatchTransfer/BatchTransferManager/StepsData.mixin";
import { currencyFilter } from "@/filters/currency.filter";
import {
  BatchTransfer,
  SourceType,
  TRANSFER_SOURCE
} from "@/interfaces/batchTransferManager";
import { Product } from "@/interfaces/product";
import { EventBus, pusherEvents } from "@/internal";
import batchTransferManagerService from "@/services/BatchTransferManager/batchTransferManager.service";
import { QuoteService } from "@/services/BatchTransferManager/Quote.service";
import { messagesService } from "@/services/messages.service";
import {
  HelixDatePickerComponent,
  TableAction,
  TableComponent,
  TableHeader,
  TablePagination,
  TablePaginationDefault,
  TableSubListComponent
} from "helix-vue-components";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import BatchTransferTableActions from "../BatchTransferTableActions.mixin";
import BatchTransferTableData from "../BatchTransferTableData.mixin";
import QuotePrintComponent from "./print/QuotePrint.component";
import Template from "./QuotesDataTable.template.vue";
import { quoteTable } from "./tableDefinition";

@Component({
  mixins: [Template],
  components: {
    TableComponent,
    TableSubListComponent,
    HelixDatePickerComponent
  },
  inject: ["$changes"]
})
export default class QuotesDataTable extends Mixins(
  BatchTransferTableData,
  BatchTransferTableActions,
  StepsData
) {
  public filters: { [key: string]: string | null } = {};
  public sourceTypes: SourceType[] = [];
  public pagination: TablePagination = { ...TablePaginationDefault };
  @Prop({ default: false }) public isActive!: boolean;
  public searchField = "";
  public isLoading = false;
  protected loaded: boolean = false;
  protected service!: QuoteService;
  private sourceScope: BatchTransfer.FilterScopeTransfer =
    BatchTransfer.defaultFilterScope;

  public get rowActions(): TableAction[] {
    return [
      {
        icon: "fal fa-ellipsis-v",
        modalActions: {
          modalNumber: 2,
          modalButtons: [
            {
              icon: "fal fa-print",
              action: (item: any) => {
                const transfer = this.rawData.find(t => item.id === t.id);
                this.print(transfer!, QuotePrintComponent);
              },
              visibleCondition: () =>
                this.hasPermission(this.policyList.printQuote)
            },
            {
              icon: "fal fa-pen",
              visibleCondition: () =>
                this.hasPermission(this.policyList.modifyQuote),
              action: (item: any) => {
                const transfer = this.rawData.find(t => item.id === t.id);
                this.edit(transfer!);
              }
            },
            {
              icon: "fal fa-trash-alt",
              visibleCondition: () =>
                this.hasPermission(this.policyList.deleteQuote),
              action: (item: any) => {
                const transfer = this.rawData.find(t => item.id === t.id);
                this.void(transfer!.id!);
              }
            }
          ]
        }
      }
    ];
  }
  public get source() {
    const sources: { [key: string]: any } = {
      VENDOR_TRANSFER: {
        placeholder: this.$tc("batch_transfer_manager_module.labels.vendor", 1),
        items:
          (this.sourceScope.vendors.length && this.sourceScope.vendors) ||
          this.defaultListVendors
      },
      LOCATION_TRANSFER: {
        placeholder: this.$tc(
          "batch_transfer_manager_module.labels.location",
          1
        ),
        items:
          (this.sourceScope.locations.length && this.sourceScope.locations) ||
          this.defaultListLocations
      }
    };
    return this.filters.type
      ? sources[this.filters.type]
      : {
          placeholder: this.$t(
            "batch_transfer_manager_module.labels.select_type_first"
          ),
          items: []
        };
  }

  public updateSource() {
    if (this.filters.type === TRANSFER_SOURCE.LOCATION_TRANSFER) {
      this.sourceScope.locations = this.getterLocations;
    } else {
      this.sourceScope.vendors = this.getterVendors;
    }
    this.isLoading = false;
  }

  @Watch("searchField")
  public async search(value: string) {
    if (value !== null) {
      this.isLoading = true;
      await this.loadDebounce(
        value,
        (this.filters.type === TRANSFER_SOURCE.LOCATION_TRANSFER &&
          this.searchLocation) ||
          this.searchVendor,
        this.updateSource
      );
    }
  }

  @Watch("isActive", { immediate: true }) public onActiveChange() {
    if (this.isActive) {
      this.$changes.watch(
        [pusherEvents.transferTouched, pusherEvents.quoteTouched],
        this.fetchData
      );

      if (!this.loaded) {
        this.fetchData();
        this.loaded = true;
      }
    }
  }

  public clearSourceFilter() {
    this.searchField = "";
    this.filters.source = null;
    this.applyFilters();
  }

  public async loadItems(data: { index: number }) {
    if (!this.tableDefs.data[data.index].visited) {
      this.tableDefs.subcomponent.loading = true;
      const order = this.tableDefs.data[data.index];
      const detail: BatchTransfer.Quote | null = await this.service.getById(
        String(order.id)
      );

      if (detail) {
        this.tableDefs.data[data.index].items = detail.items.map(item => {
          return {
            product: (item.product as Product).name,
            quantity: item.quantity,
            total: currencyFilter(item.prices.total)
          };
        });
      }
      this.tableDefs.subcomponent.loading = false;
      this.tableDefs.data[data.index].visited = true;
    }
  }
  protected mounted() {
    EventBus.$on("BatchTransferListTouched", (transferType: string) => {
      if (transferType === "outbound-transfer") {
        this.loaded = false;
      }
    });
    this.service = batchTransferManagerService.service("quote") as QuoteService;
    this.sourceTypes = [
      {
        label: String(
          this.$tc("batch_transfer_manager_module.labels.vendor", 1)
        ),
        value: TRANSFER_SOURCE.VENDOR_TRANSFER
      },
      {
        label: String(
          this.$tc("batch_transfer_manager_module.labels.location", 1)
        ),
        value: TRANSFER_SOURCE.LOCATION_TRANSFER
      }
    ];
  }

  protected sort(header: TableHeader) {
    this.sortAndFetch(header, this.fetchData);
  }

  protected async applyFilters() {
    this.query = {
      ...this.query,
      "q[created_at_greater_than_or_equal]": this.filters.start_date!,
      "q[created_at_less_than_or_equal]": this.filters.end_date!,
      "q[targetable_type_equals]": this.filters.type!,
      "q[targetable_id_equals]": this.filters.source!,
      page: this.pagination.currentPage,
      per_page: this.pagination.itemsPerPage
    };
    this.fetchData();
  }

  protected changePagination(pagination: TablePagination) {
    this.pagination = pagination;
    this.applyFilters();
    this.loaded = true;
  }

  protected async fetchData() {
    this.tableDefs.loading = true;
    const response = await this.service.get(this.query);
    this.pagination = response.pagination;
    this.rawData = response.data;
    this.mapData(quoteTable, this.rawData);
    this.tableDefs.loading = false;
  }

  protected async void(id: number) {
    try {
      await this.service.void(id);
      this.fetchData();
    } catch (e) {
      messagesService.showMessage(
        "fal fa-exclamation-triangle",
        String(
          this.$t("batch_transfer_manager_module.messages.something_went_wrong")
        ),
        "error"
      );
    }
  }
}
